// Alle animaties die het dashboard kan afspelen, op één plek.
//
// id     - waar de CSS en de animatielaag naar kijken
// key    - toets waarmee je hem afspeelt (animatielab en dashboard)
// name   - hoe hij in het lab in de lijst staat
// hint   - wanneer hij in het echt te zien is
// ms     - hoe lang hij duurt; daarna ruimt de hook hem op
// target - "seller": licht ook de rij/trede van die verkoper op
// demo   - voorbeeldgegevens voor het handmatig afspelen: een toets en de
//          rondleiding ("alles achter elkaar") gebruiken deze, zodat je ziet
//          wát er verkocht is. play() zelf mengt ze niet mee: bij een echte
//          verkoop hoort alleen te staan wat er echt verkocht is.

import { chargeMs } from "./charge";
import { missedSalesMs } from "./missedSales";
import { targetChargeMs } from "./targetCharge";
import {
  partyBlocksMs,
  partyCannonMs,
  partyHypeMs,
  partyLightsMs,
  partyLineMs,
  partyMs,
  targetPartyMs,
  targetSongMs,
} from "./party";

export const animations = [
  {
    id: "sale-charge",
    key: "c",
    name: "Opladen",
    hint: "De aanloop vlak voor een verkoop in beeld komt",
    // De blokjes lopen vol en elk blokje is een tik die je hoort; de tijden
    // staan in ./charge.js, zodat beeld en geluid op dezelfde tel lopen. De
    // aankondiging begint al voor dit weg is, zie chargeHandoff in
    // hooks/useSaleEvents.js: zo blijft het donker en valt er geen gat.
    ms: chargeMs,
  },
  {
    id: "sale-splash",
    key: "n",
    name: "Nieuwe verkoop",
    hint: "Het beeld gaat donker: wie, wat en hoeveel",
    // 0,4 donker + de regels erin + staan + 0,5 weg; de tijden in
    // animations.css horen hierbij. Bij een echte verkoop lichten de rij en
    // de meter pas op als dit weg is, zie saleGroup in hooks/useSaleEvents.js.
    // 9s: lang genoeg dat wie pas bij de aanloop opkeek het nog kan lezen.
    ms: 9000,
    demo: { name: "Ernst", amount: 1495, service: "Managed Cloud Backup" },
  },
  {
    id: "row-flash",
    key: "2",
    name: "Rij oplichten",
    hint: "Wijst aan wíe er verkocht heeft",
    ms: 1800,
    target: "seller",
    demo: { name: "Ernst" },
  },
  {
    id: "confetti-burst",
    key: "3",
    name: "Confetti",
    hint: "Grote deal of mijlpaal",
    // Laatste snipper: 1.6s wachten + 4.6s vallen. Korter en de laag
    // verdwijnt terwijl er nog dingen onderin het beeld hangen.
    ms: 6600,
  },
  {
    id: "personal-best",
    key: "4",
    name: "Eigen record",
    hint: "Iemands grootste verkoop tot nu toe",
    // Dezelfde opbouw als de recordkaart bij het vuurwerk, maar kleiner en
    // korter: dit komt vaker voorbij dan een record, dus het mag het beeld
    // niet even lang bezet houden. De regels komen na elkaar binnen; de
    // tijden in animations.css horen hierbij.
    ms: 6500,
    demo: {
      name: "Ernst",
      amount: 1850,
      service: "Managed Cloud Backup",
      previous: 1200,
    },
  },
  {
    id: "record-stamp",
    key: "x",
    name: "Record-stempel",
    hint: "Slaat op de aankondiging als de verkoop een record is",
    // Klap in 0,45s, dan staan, en de laatste 0,4s weg met de aankondiging
    // mee. De tijden in animations.css horen hierbij; wanneer hij komt staat
    // bij stampAfterSplash in hooks/useSaleEvents.js.
    ms: 1900,
    demo: { kind: "best" },
  },
  {
    id: "rank-up",
    key: "5",
    name: "Stijger",
    hint: "Iemand schuift op in de ranglijst",
    ms: 3600,
    demo: { name: "Ernst", position: 2, from: 4 },
  },
  {
    id: "gauge-pulse",
    key: "6",
    name: "Meter pulseert",
    hint: "Het teamtotaal loopt op",
    ms: 2400,
  },
  {
    id: "screen-glow",
    key: "7",
    name: "Schermrand",
    hint: "Het seintje voor een verkoop in beeld komt",
    // Vijf seconden, en daarna begint de aanloop meteen; zie glowLead in
    // hooks/useSaleEvents.js en de tijd in animations.css.
    ms: 5000,
  },
  {
    id: "fireworks",
    key: "8",
    name: "Vuurwerk",
    hint: "Grootste losse verkoop ooit",
    // Laatste knal: 1.6s wachten + 1.5s vliegen; de tekst blijft 7,5s staan
    // zodat je hem ook echt kunt lezen.
    ms: 7500,
    demo: {
      name: "Ernst",
      amount: 4250,
      service: "Managed Cloud Backup",
      previous: 3200,
    },
  },
  {
    id: "podium-bounce",
    key: "9",
    name: "Podium veert",
    hint: "De top 3 verandert van volgorde",
    ms: 1800,
  },
  {
    id: "target-charge",
    key: "t",
    name: "Meter naar 100",
    hint: "De aanloop naar het target: de wijzer loopt vol",
    // De wijzer en de tikken rekenen met dezelfde tijden, die staan in
    // ./targetCharge.js. De kaart "GEHAALD" begint voor dit weg is, zie
    // targetPartyGroup in ./party.js: zo blijft het donker en valt er geen gat.
    ms: targetChargeMs,
  },
  {
    id: "target-hit",
    key: "0",
    name: "Target gehaald",
    hint: "Het team tikt de 100% aan",
    ms: 9500,
    demo: { amount: 85000 },
  },
  {
    id: "target-party-dim",
    key: "v",
    name: "Target: feestlicht",
    hint: "Het beeld zakt een tikje weg zolang het targetfeest duurt",
    // Van "GEHAALD" tot het nummer uit is; zie targetPartyGroup in
    // ./party.js. De CSS krijgt deze duur mee via --fx-target-dim-ms.
    ms: targetPartyMs,
    tour: false,
  },
  {
    // Geen beeld, alleen het lange nummer onder het targetfeest. Het begint
    // als de meter de 100 raakt, meteen op de klap; zie targetPartyGroup in
    // ./party.js en "target-song" in audio/samples.js.
    id: "target-song",
    key: "u",
    name: "Target: nummer",
    hint: "Het lange feestnummer onder het targetfeest",
    ms: targetSongMs,
    tour: false,
  },
  {
    id: "missed-sales",
    key: "m",
    name: "Verkopen gemist",
    hint: "Na het aanzetten: wat er verkocht is terwijl de tv uit stond",
    // De kaart somt de verkopen één voor één op, dus hoe lang hij staat
    // hangt af van hoeveel het er zijn: zie msFor. ms is de duur van het
    // voorbeeld hieronder, voor het lab.
    ms: missedSalesMs(3),
    msFor: (payload) => missedSalesMs(payload.sales?.length ?? 0),
    // since: hoe laat het scherm uitging. Vast op gisteravond, zodat de
    // kaart in het lab ook "gisteren" zegt.
    demo: {
      since: Date.now() - 15 * 60 * 60 * 1000,
      amount: 3525,
      sales: [
        { name: "Ernst", amount: 1495, service: "Managed Cloud Backup" },
        { name: "Bert", amount: 1250, service: "Microsoft 365" },
        { name: "Hans", amount: 780, service: "Werkplekbeheer" },
      ],
    },
  },
  {
    // Dezelfde kaart, maar dan zonder verkopen erop. Live komt dit geval
    // ook uit "missed-sales" (de kaart kijkt zelf of de lijst leeg is); dit
    // is een eigen regel zodat je hem in het lab en op de tv los kunt
    // bekijken, want hij verschijnt vanzelf alleen op een ochtend waarop er
    // niets verkocht is en dat wil je niet af hoeven wachten.
    id: "missed-nothing",
    key: "l",
    name: "Niks gemist",
    hint: "Na het aanzetten: er is niets verkocht terwijl de tv uit stond",
    ms: missedSalesMs(0),
    demo: {
      since: Date.now() - 15 * 60 * 60 * 1000,
      amount: 0,
      sales: [],
    },
  },
  {
    // De vijf hieronder horen bij elkaar en spelen normaal samen af, zie
    // partyGroup in ./party.js: Shift+8, of typ "feest" op de tv. Ze staan
    // hier los zodat je ze in het lab ook apart kunt bekijken - dansen
    // zonder muziek erbij is een stuk makkelijker beoordelen.
    id: "party-dance",
    key: "f",
    name: "Feest: dansen",
    hint: "Het hele dashboard gaat op de maat mee",
    // Zo lang als het nummer duurt; het geluid hangt hier aan vast, zie
    // sampleOptions in audio/samples.js. Het target danst langer, op zijn
    // eigen nummer, en geeft die duur mee (zie targetPartyGroup).
    ms: partyMs,
    msFor: (payload) => payload.ms ?? partyMs,
    tour: false,
  },
  {
    id: "party-blocks",
    key: "g",
    name: "Feest: banen",
    hint: "Vlakke banen kleur die over het beeld schuiven",
    ms: partyBlocksMs,
    tour: false,
  },
  {
    id: "party-hype",
    key: "d",
    name: "Feest: aanmoediging",
    hint: "De balk onderin die regels doorbladert",
    // Zoveel regels, zoveel beurten; het target heeft er een eigen lijst.
    ms: partyHypeMs,
    msFor: (payload) =>
      payload.lines ? payload.lines.length * partyLineMs : partyHypeMs,
    tour: false,
  },
  {
    id: "party-lights",
    key: "b",
    name: "Feest: lichtshow",
    hint: "Lichtbundels van boven die op de maat zwaaien",
    ms: partyLightsMs,
    msFor: (payload) => payload.ms ?? partyLightsMs,
    tour: false,
  },
  {
    id: "party-cannons",
    key: "k",
    name: "Feest: kanonnen",
    hint: "Confetti vanuit beide hoeken onderin omhoog geschoten",
    ms: partyCannonMs,
    tour: false,
  },
];

// De rondleiding: alles achter elkaar, met de spatiebalk. Het feestje doet
// daar niet aan mee. De rondleiding knipt elke animatie na drie seconden af
// om door te kunnen, en het nummer laat zich niet afknippen: dat zou een
// kwart minuut onder de rest door blijven spelen. Los bekijken kan gewoon,
// met de knop in het lab of met Shift+8.
export const tourAnimations = animations.filter((animation) => animation.tour !== false);

// Hoe lang een run duurt. Meestal gewoon ms; een animatie die langer staat
// naarmate er meer op staat (de kaart "Verkopen gemist") rekent dat uit met
// msFor op basis van wat hij te zien krijgt.
export const durationOf = (animation, payload = {}) =>
  animation.msFor ? animation.msFor(payload) : animation.ms;

export const byId = (id) => animations.find((a) => a.id === id) || null;
export const byKey = (key) => animations.find((a) => a.key === key) || null;
